import { Controller } from "@hotwired/stimulus"

// Click-to-edit description (Plane-style) + FD-239 stale-edit guard.
//
//   <div data-controller="inline-edit"
//        id="desc-guard-…"                          ← stable id so a morph reuses this
//        data-inline-edit-lock-version-value="3"     ← rides live; morph fires the change cb
//        data-inline-edit-actor-value="Maya Chen">
//     <div data-inline-edit-target="banner" hidden>…Reload / Keep editing…</div>
//     <div data-inline-edit-target="affordance" id="desc-edit-…">   ← frozen only while editing
//       <div data-inline-edit-target="display" data-action="click->inline-edit#edit">…</div>
//       <div data-inline-edit-target="editor" hidden>
//         <form data-action="submit->inline-edit#save">…</form>
//       </div>
//     </div>
//   </div>
//
// Only `display` and `editor` are required. `affordance`, `banner`/`bannerText` and the
// lockVersion conflict machinery are OPTIONAL — the work-item description is the only surface that
// supplies them; the property-strip pills, subitem rows and the stakeholders row editor use the
// bare display/editor pair. Reach for an optional target through hasXTarget, never through
// `this.xTarget?.…`: Stimulus' generated getter THROWS on a missing target, and the throw happens
// while evaluating the getter, before `?.` is ever consulted (FD-640).
//
// When NOT editing, the affordance morphs live with the rest of the detail (FD-238). On edit
// we freeze it with data-turbo-permanent so a broadcast-refresh can't reset the Tiptap surface;
// lockVersion rides as a Stimulus value, so a morph that advances it while we're editing means
// another actor changed the item under us — we raise a conflict banner instead of silently
// diverging. Saving (our own write) drops the freeze first so the version bump isn't a "conflict".
export default class extends Controller {
  static targets = ["display", "editor", "affordance", "banner", "bannerText"]
  static values = { lockVersion: Number, actor: String }

  edit(event) {
    // FD-1190: measure the display before it hides: where its text starts, and where in
    // that text the click landed.
    const start = textStart(this.displayTarget)
    const offset = clickOffset(event, this.displayTarget, start)
    this.displayTarget.hidden = true
    this.editorTarget.hidden = false
    this.editing = true
    this.baselineVersion = this.lockVersionValue
    // Freeze the edit affordance against live morphs (id-scoped, restored on save/cancel).
    if (this.hasAffordanceTarget) this.affordanceTarget.setAttribute("data-turbo-permanent", "")
    const block = this.editorTarget.querySelector(".fd-block-surface .ProseMirror")
    if (block) {
      // FD-1190: the editor's toolbar pushes the text down. Scroll by the same amount so the
      // line that was clicked stays where it was, under the pointer.
      const shift = textStart(block).top - start.top
      const pane = scrollParent(this.element)
      if (pane && shift) pane.scrollTop += shift
      requestAnimationFrame(() => this.#focusEditor(block, offset))
    } else {
      // Skip hidden inputs — form_with emits _method and authenticity_token first, so a bare
      // "input, textarea" focuses one of those and the caret never reaches the real field.
      // FD-1190: the field opens in place of the display that was just clicked, on screen.
      this.editorTarget.querySelector("input:not([type=hidden]), textarea")?.focus({ preventScroll: true })
    }
  }

  // FD-1190: a bare DOM focus() put the caret at the very start and scrolled there. Tiptap's
  // focus places the caret without scrolling: at the clicked spot, else at the end.
  #focusEditor(block, offset) {
    const host = block.closest("[data-controller~='block-editor']")
    const editor = host && this.application.getControllerForElementAndIdentifier(host, "block-editor")?.editor
    if (!editor) return block.focus({ preventScroll: true })
    editor.commands.focus(this.#clickedPos(editor, block, offset) ?? "end", { scrollIntoView: false })
  }

  // The document position under the clicked spot, mapped onto the editor's text.
  #clickedPos(editor, block, offset) {
    if (!offset) return null
    const start = textStart(block)
    const hit = editor.view.posAtCoords({ left: start.left + offset.x, top: start.top + offset.y })
    if (!hit) return null
    // Beside an image or a table, posAtCoords lands between blocks; a caret needs text.
    const doc = editor.state.doc
    for (const pos of [hit.pos, hit.pos + 1, hit.pos - 1]) {
      if (pos > 0 && pos <= doc.content.size && doc.resolve(pos).parent.inlineContent) return pos
    }
    return null
  }

  // Cancel must DISCARD, not merely hide. Hiding alone leaves the typed values live in the inputs:
  // the display row re-appears showing the stored text (it is server-rendered, not form-bound), so
  // the edit looks discarded — but reopening the row and saving anything would silently write the
  // "cancelled" value. reset() restores each control to the value attribute the server rendered,
  // which is the stored record. (After a 422 the server deliberately rendered the SUBMITTED values,
  // so reset there restores those; that edit is still visibly rejected rather than silently kept.)
  //
  // That holds for ordinary controls (the pills' and stakeholder row's text inputs), NOT for a
  // Tiptap surface: the ProseMirror document isn't a control at all, and its hidden Action Text
  // input is in "default mode" — the editor's syncs have rewritten the very value attribute
  // reset() restores from. block_editor_controller listens for this same `reset` event and puts
  // both back to the HTML it was mounted with, so the whole editor is discarded too.
  cancel(event) {
    event?.preventDefault()
    this.stopEditing()
    this.editorTarget.querySelector("form")?.reset()
    this.editorTarget.hidden = true
    this.displayTarget.hidden = false
  }

  // Our own submit: drop edit mode + the freeze so the saved description morphs in cleanly and
  // the version bump from this write isn't mistaken for someone else's change. Does not
  // preventDefault — the form still submits (Turbo redirect → morph).
  save() {
    this.stopEditing()
  }

  // A morph advanced lock_version. If we're mid-edit on a different baseline, the in-flight
  // edit is now stale — surface the conflict banner (the human face of the 409).
  lockVersionValueChanged(value, previous) {
    if (previous === undefined) return       // initial assignment on connect
    if (!this.editing) return                // idle → the morph already showed us the latest
    if (value === this.baselineVersion) return
    this.showConflict()
  }

  showConflict() {
    if (this.hasBannerTextTarget) {
      const actor = this.actorValue && this.actorValue !== "System" ? this.actorValue : "Someone"
      this.bannerTextTarget.textContent =
        `${actor} changed this item since you started editing — reload to see their version, or keep editing to overwrite.`
    }
    if (this.hasBannerTarget) this.bannerTarget.hidden = false
  }

  // Keep editing: hide the banner and re-baseline so we don't re-warn on the same version;
  // a further external change re-warns.
  dismiss() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = true
    this.baselineVersion = this.lockVersionValue
  }

  // Discard the in-flight edit and load the latest server state. A hard reload (not
  // Turbo.visit) is deliberate: with the global morph refresh method, a same-url visit
  // would morph — and the still-frozen (data-turbo-permanent) affordance would survive,
  // preserving the very edit we're trying to discard. A full reload drops it cleanly.
  reload() {
    this.stopEditing()
    window.location.reload()
  }

  stopEditing() {
    this.editing = false
    if (this.hasAffordanceTarget) this.affordanceTarget.removeAttribute("data-turbo-permanent")
    if (this.hasBannerTarget) this.bannerTarget.hidden = true
  }
}

// FD-1190: where an element's text starts on screen: the left of its content box, and the
// top of its first line of text. The display keeps its first paragraph's top margin and the
// editor drops it, so the two boxes' tops don't line up but their first lines do.
function textStart(el) {
  const rect = el.getBoundingClientRect()
  const style = getComputedStyle(el)
  const start = {
    left: rect.left + el.clientLeft + parseFloat(style.paddingLeft),
    top: rect.top + el.clientTop + parseFloat(style.paddingTop),
  }
  const text = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  }).nextNode()
  if (text) {
    const range = document.createRange()
    range.selectNodeContents(text)
    const line = range.getClientRects()[0]
    if (line) start.top = line.top
  }
  return start
}

// FD-1190: a real pointer click's offset from where the element's text starts, or null (a
// keyboard or scripted click, whose coordinates don't point at the text).
function clickOffset(event, el, start) {
  if (!event || !event.detail || event.clientX == null) return null
  const rect = el.getBoundingClientRect()
  if (event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom) return null
  return { x: event.clientX - start.left, y: event.clientY - start.top }
}

// FD-1190: the nearest ancestor that actually scrolls (the layout pane, the peek drawer, a modal).
function scrollParent(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node
  }
  return document.scrollingElement
}
